export const fmtPrice = (price, transaction) => {
  const n = Number(price || 0);
  const s = new Intl.NumberFormat("fr-FR").format(n);
  if (transaction === "Location") return `€ ${s} / mois`;
  return `€ ${s}`;
};

// Normalize image URLs and inject Cloudinary transformations when available.
// `width` (optional) — target display width in CSS pixels. Cloudinary will auto-serve
// WebP/AVIF + quality-optimised + resized variants. Absolute (http/data) URLs are
// returned as-is for non-Cloudinary hosts (e.g. Unsplash).
export const imgUrl = (src, width) => {
  if (!src) return "";
  // Cloudinary — inject transforms after `/image/upload/`
  if (src.includes("res.cloudinary.com") && src.includes("/image/upload/")) {
    const parts = ["f_auto", "q_auto:good"];
    if (width) parts.push(`w_${width}`, "c_limit");
    const transform = parts.join(",");
    return src.replace("/image/upload/", `/image/upload/${transform}/`);
  }
  // Unsplash — rewrite the `w` param so phones get small variants (huge perf win)
  if (src.includes("images.unsplash.com") && width) {
    try {
      const u = new URL(src);
      u.searchParams.set("w", String(width));
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      if (!u.searchParams.has("q")) u.searchParams.set("q", "80");
      return u.toString();
    } catch {
      return src;
    }
  }
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

export const TYPES = ["Maison", "Appartement", "Penthouse", "Terrain", "Commercial"];
export const TRANSACTIONS = ["Vente", "Location"];

// Grouped localities for <optgroup>. Country code is appended in () so values
// remain unique once stored, and searches can still match on the city name.
export const LOCATION_GROUPS = [
  {
    country: "Luxembourg",
    code: "LU",
    cities: [
      // Capitale & agglomération
      "Luxembourg-Ville", "Bonnevoie", "Belair", "Limpertsberg", "Merl", "Cessange", "Cents", "Gasperich", "Kirchberg", "Clausen", "Grund", "Pfaffenthal", "Pulvermühl", "Weimerskirch", "Beggen", "Dommeldange", "Eich", "Rollingergrund", "Hollerich", "Gare",
      // Centre
      "Hesperange", "Alzingen", "Fentange", "Howald", "Itzig",
      "Strassen", "Bertrange", "Walferdange", "Helmsange", "Bereldange",
      "Steinsel", "Heisdorf", "Mullendorf",
      "Niederanven", "Senningen", "Senningerberg", "Oberanven",
      "Contern", "Moutfort", "Medingen", "Oetrange",
      "Leudelange", "Kopstal", "Bridel",
      "Kehlen", "Olm", "Keispelt", "Meispelt", "Nospelt",
      // Sud (bassin minier & frontière)
      "Esch-sur-Alzette", "Lallange", "Belval",
      "Differdange", "Oberkorn", "Niederkorn",
      "Dudelange", "Budersberg",
      "Pétange", "Rodange", "Lamadelaine",
      "Sanem", "Soleuvre", "Belvaux", "Ehlerange",
      "Schifflange",
      "Rumelange",
      "Kayl", "Tétange",
      "Bettembourg", "Huncherange", "Noertzange", "Abweiler",
      "Käerjeng", "Bascharage", "Clemency", "Fingig", "Hautcharage", "Linger",
      "Mondercange", "Bergem", "Foetz", "Pontpierre",
      "Roeser", "Crauthem", "Bivange", "Berchem", "Peppange", "Kockelscheuer",
      "Dippach", "Schouweiler", "Sprinkange",
      "Reckange-sur-Mess", "Ehlange", "Limpach",
      "Frisange", "Hellange", "Aspelt",
      "Weiler-la-Tour", "Syren",
      // Ouest
      "Mamer", "Capellen", "Holzem",
      "Mersch", "Beringen", "Rollingen", "Moesdorf",
      "Steinfort", "Hagen", "Kleinbettingen",
      "Koerich", "Goeblange", "Goetzingen",
      "Septfontaines", "Simmern",
      "Garnich", "Hivange", "Kahler",
      "Hobscheid", "Eischen",
      "Boevange-sur-Attert", "Brouch", "Buschdorf", "Finsterthal",
      "Tuntange", "Hollenfels", "Marienthal",
      "Saeul", "Useldange", "Rippweiler",
      "Beckerich", "Elvange", "Levelange",
      "Ell", "Petit-Nobressart",
      // Est (Moselle viticole)
      "Grevenmacher", "Remich", "Stadtbredimus", "Bech-Kleinmacher",
      "Echternach", "Rosport", "Mompach", "Berdorf", "Consdorf",
      "Mondorf-les-Bains", "Altwies", "Ellange",
      "Wormeldange", "Ahn", "Machtum",
      "Junglinster", "Gonderange", "Graulinster", "Rodenbourg",
      "Betzdorf", "Roodt-sur-Syre", "Mensdorf", "Olingen",
      "Bous", "Assel",
      "Lenningen", "Canach", "Greiveldange",
      "Dalheim", "Filsdorf", "Welfrange",
      "Schengen", "Remerschen", "Wintrange",
      "Waldbredimus", "Trintange",
      "Flaxweiler", "Gostingen",
      "Manternach", "Berbourg", "Lellig",
      "Mertert", "Wasserbillig",
      // Nord (Éislek / Ardennes)
      "Diekirch", "Ettelbruck",
      "Wiltz", "Clervaux", "Troisvierges", "Drauffelt", "Munshausen",
      "Vianden", "Putscheid",
      "Redange", "Ospern", "Reichlange",
      "Feulen", "Niederfeulen", "Oberfeulen",
      "Colmar-Berg", "Welsdorf",
      "Parc Hosingen", "Hosingen", "Holzthum", "Consthum",
      "Kiischpelt", "Wilwerwiltz", "Enscherange",
      "Goesdorf", "Dahl", "Merscheid", "Bonnal",
      "Winseler", "Berle", "Doncols",
      "Vichten", "Préizerdaul", "Bettborn", "Platen",
      "Grosbous", "Dellen",
      "Mertzig", "Wahl", "Heiderscheid",
      "Esch-sur-Sûre", "Eschdorf", "Heiderscheidergrund",
      "Rambrouch", "Bigonville", "Perlé", "Arsdorf",
      "Boulaide", "Baschleiden", "Surré",
      "Ermsdorf", "Medernach", "Reisdorf",
      "Nommern", "Cruchten", "Schrondweiler",
      "Fischbach", "Angelsberg", "Schoos",
      "Larochette", "Ernzen",
      "Beaufort", "Grundhof",
      "Bourscheid", "Welscheid",
      "Schieren",
      "Erpeldange-sur-Sûre",
      "Bettendorf", "Moestroff",
      "Tandel", "Bastendorf",
      "Stolzembourg", "Wahlhausen",
      "Weiswampach", "Beiler", "Leithum",
      "Wincrange", "Asselborn", "Troine", "Hoffelt",
      // Générique
      "Autre localité (Luxembourg)",
    ],
  },
  {
    country: "Belgique",
    code: "BE",
    cities: [
      // Province du Luxembourg (frontalier)
      "Arlon", "Attert", "Bonnert", "Heinsch", "Sterpenich", "Weyler",
      "Aubange", "Athus", "Rodange (BE)", "Halanzy",
      "Messancy", "Hondelange", "Turpange", "Wolkrange",
      "Musson", "Baranzy", "Signeulx",
      "Saint-Léger", "Meix-devant-Virton",
      "Virton", "Ethe", "Latour",
      "Rouvroy", "Torgny",
      "Habay", "Habay-la-Neuve", "Habay-la-Vieille", "Hachy",
      "Martelange", "Fauvillers",
      "Léglise", "Mellier",
      "Étalle", "Sainte-Marie",
      "Tintigny", "Rossignol", "Bellefontaine",
      "Florenville", "Chiny", "Izel",
      "Bertrix", "Herbeumont",
      "Bastogne", "Wardin", "Houffalize",
      "Vaux-sur-Sûre", "Sibret",
      "Libramont-Chevigny", "Saint-Hubert",
      "Neufchâteau",
      "Marche-en-Famenne", "Nassogne", "Rendeux",
      "Vielsalm", "Gouvy", "Burg-Reuland",
      "La Roche-en-Ardenne",
      "Durbuy",
      "Bouillon",
      // Bruxelles & grandes villes (pour investissement)
      "Bruxelles", "Liège", "Namur", "Mons",
      "Autre localité (Belgique)",
    ],
  },
  {
    country: "France",
    code: "FR",
    cities: [
      // Frontière luxembourgeoise (Moselle / Meurthe-et-Moselle)
      "Thionville", "Yutz", "Hettange-Grande", "Zoufftgen",
      "Mondorff", "Évrange", "Basse-Rentgen", "Boust",
      "Escherange", "Volmerange-les-Mines", "Rédange",
      "Audun-le-Tiche", "Russange", "Ottange", "Boulange",
      "Aumetz", "Villerupt", "Thil", "Crusnes",
      "Longwy", "Longlaville", "Mont-Saint-Martin", "Haucourt-Moulaine",
      "Herserange", "Réhon", "Saulnes",
      "Cosnes-et-Romain", "Lexy", "Chenières",
      "Cutry", "Laix", "Tiercelet",
      "Mexy", "Villers-la-Montagne",
      "Briey", "Jarny", "Homécourt",
      "Metz", "Montigny-lès-Metz", "Woippy", "Marly",
      "Nancy", "Vandœuvre-lès-Nancy", "Laxou",
      "Fameck", "Florange", "Hayange", "Nilvange", "Serémange-Erzange",
      "Uckange", "Rombas", "Amnéville",
      "Sierck-les-Bains", "Rettel", "Contz-les-Bains",
      "Cattenom", "Koenigsmacker",
      "Bouzonville",
      "Verdun", "Bar-le-Duc",
      "Strasbourg", "Mulhouse", "Colmar",
      // Capitales régionales populaires
      "Paris", "Lyon", "Lille", "Bordeaux", "Marseille", "Nice",
      "Autre localité (France)",
    ],
  },
  {
    country: "Allemagne",
    code: "DE",
    cities: [
      // Frontière luxembourgeoise (Sarre & Rhénanie-Palatinat)
      "Trier", "Trèves", "Konz", "Wasserbillig (DE)",
      "Wellen", "Nittel", "Temmels", "Mannebach",
      "Palzem", "Saarburg", "Ayl", "Ockfen",
      "Perl", "Nennig", "Besch", "Sinz", "Borg", "Tettingen-Butzdorf",
      "Oberleuken", "Mettlach", "Orscholz",
      "Merzig", "Losheim am See", "Wadern", "Nonnweiler",
      "Saarbrücken", "Saarlouis", "Dillingen/Saar", "Völklingen",
      "Bitburg", "Bollendorf", "Ralingen", "Echternacherbrück",
      "Idar-Oberstein",
      "Prüm", "Gerolstein",
      "Wittlich", "Bernkastel-Kues",
      "Koblenz", "Kaiserslautern",
      "Berlin", "Munich", "Cologne", "Frankfurt",
      "Autre localité (Allemagne)",
    ],
  },
];

// Flat list for backward compatibility + full-text search — preserves unique city
// names across all countries. We dedupe while keeping order.
export const LOCATIONS = Array.from(
  new Set(LOCATION_GROUPS.flatMap((g) => g.cities)),
);
